import { ChevronRight, Settings } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../Shared/Accordion/Accordion';
import { Button } from '../../Shared/Button/Button';
import {
  AnimatedCollapsibleContent,
  Collapsible,
  CollapsibleTrigger,
} from '../../Shared/Collapsible/Collapsible';
import { Input } from '../../Shared/Input/Input';
import { ApiDataTable, HttpItemCategory } from '../ApiDataTable/ApiDataTable';
import { McpCatalogPicker } from './McpCatalogPicker';
import { RemoteMcpOAuthStatus } from './RemoteMcpOAuthStatus';
import { MCP_CATALOG_ICON_COLOR, McpCatalogEntry } from './mcpCatalog';
import { RemoteMcpDto } from '@/Domain/api/Action/dto/RemoteMCP.dto';
import { Form, FormControl, FormField, FormItem } from '@/Domain/components/Shared/Form/Form';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { PostHogEventEnum, type RemoteMcpEntryPoint } from '@/Domain/enum/PostHogEvent.enum';
import { useConnectRemoteMcpActionForm } from '@/Domain/hooks/Action/RemoteMcp/useConnectRemoteMcpActionForm.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { cn } from '@/Domain/utils/cn';

type ConnectHook = ReturnType<typeof useConnectRemoteMcpActionForm>;

// Lets people nudge us to add a server that is not in the catalog: opens a pre-filled email to
// support with a short stub. The subject/body stay English for the support inbox; only the link
// label is translated.
const MCP_SUGGESTION_MAILTO = `mailto:support@quickchat.ai?subject=${encodeURIComponent(
  'MCP integration suggestion',
)}&body=${encodeURIComponent(
  [
    'Hi Quickchat team,',
    '',
    'It would be great to connect this MCP server or service:',
    '',
    'Service name:',
    'Website or MCP URL (if known):',
    'What I would use it for:',
    '',
    'Thanks!',
  ].join('\n'),
)}`;

const UrlField = ({ connect, disabled }: { connect: ConnectHook; disabled: boolean }) => {
  const { t } = useTranslation('remote_mcp');
  const { form } = connect;
  return (
    <FormField
      control={form.control}
      name="remote_mcp_url"
      render={({ field, fieldState }) => {
        const showError = !!fieldState.error && !!field.value;
        return (
          <FormItem className="space-y-2">
            <span className="text-sm font-medium text-foreground">
              {t('form.remote_mcp_url_label')}
            </span>
            <FormControl>
              <Input
                data-testid="remote-mcp-action-url"
                placeholder={t('form.remote_mcp_url_placeholder')}
                disabled={disabled}
                className={cn(showError && 'border-destructive focus-visible:ring-destructive/25')}
                {...field}
              />
            </FormControl>
            <div className="min-h-[1.125rem]">
              {showError ? (
                <p className="text-sm text-destructive">{t('form.remote_mcp_url_invalid')}</p>
              ) : null}
            </div>
          </FormItem>
        );
      }}
    />
  );
};

const HeadersDisclosure = ({ connect }: { connect: ConnectHook }) => {
  const { t } = useTranslation('remote_mcp');
  const { form } = connect;
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronRight className={cn('size-4 transition-transform', open && 'rotate-90')} />
          {t('form.optional_headers')}
        </button>
      </CollapsibleTrigger>
      <AnimatedCollapsibleContent open={open} className="pt-3">
        <ApiDataTable name={HttpItemCategory.HEADERS} form={form} withInsertBadge={false} />
      </AnimatedCollapsibleContent>
    </Collapsible>
  );
};

/** The connect experience for a brand-new action: pick a server from the catalog (a logo grid
 *  that auto-connects on click) or enter a URL manually. Once a connection is under way, the
 *  authorization panel takes over in place. */
const NewConnectionFlow = ({ connect }: { connect: ConnectHook }) => {
  const { t } = useTranslation('remote_mcp');
  const { capture } = usePostHogAnalytics();
  const { form, submitFrom, isPending, oauth } = connect;
  const busy = oauth.phase === 'authorizing' || oauth.phase === 'finalizing';
  const [manual, setManual] = useState(false);

  const handleCatalogSelect = (entry: McpCatalogEntry, entryPoint: RemoteMcpEntryPoint) => {
    form.setValue('remote_mcp_url', entry.url, { shouldValidate: true });
    form.setValue('name', entry.name);
    form.setValue('description', entry.description);
    form.setValue('icon', entry.icon);
    form.setValue('icon_color', MCP_CATALOG_ICON_COLOR);
    form.setValue('headers', []);
    void submitFrom(entryPoint);
  };

  // A connection is under way (probe finished → OAuth needed, or authorizing): show the panel.
  if (oauth.phase !== 'idle') {
    return (
      <Form {...form}>
        <div className="flex w-full flex-col gap-4">
          <UrlField connect={connect} disabled={busy} />
          <RemoteMcpOAuthStatus
            phase={oauth.phase}
            providerLabel={oauth.providerLabel}
            onConnect={oauth.openConsentPopup}
            onReconnect={oauth.reconnect}
            onDisconnect={oauth.disconnect}
            onCancel={oauth.cancel}
            isDisconnecting={oauth.isDisconnecting}
          />
        </div>
      </Form>
    );
  }

  // The connect probe is running (from a catalog tile or the manual Connect button). Show the URL
  // we're connecting to right away so a catalog click feels instant while the probe runs.
  if (isPending) {
    const target = form.getValues('name') || form.getValues('remote_mcp_url');
    return (
      <Form {...form}>
        <div className="flex w-full flex-col gap-4">
          <UrlField connect={connect} disabled />
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
            <Spinner className="size-5 shrink-0" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">
                {t('catalog.connecting', { name: target })}
              </p>
              <p className="text-xs text-muted-foreground">{t('catalog.connecting_hint')}</p>
            </div>
          </div>
        </div>
      </Form>
    );
  }

  if (manual) {
    return (
      <Form {...form}>
        <div className="flex w-full flex-col gap-4">
          <UrlField connect={connect} disabled={busy} />
          <HeadersDisclosure connect={connect} />
          <Button
            variant="dark"
            className="w-full"
            onClick={() => void submitFrom('manual')}
            data-testid="remote-mcp-action-sheet-connect-button"
          >
            {t('button.connect')}
          </Button>
          <button
            type="button"
            onClick={() => setManual(false)}
            className="cursor-pointer text-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t('catalog.browse')}
          </button>
        </div>
      </Form>
    );
  }

  // The catalog is the browse view: fill the whole sheet (full width and height) rather than sit
  // in a small box, so more services are visible at once.
  return (
    <Form {...form}>
      <div className="flex h-full w-full flex-col gap-4">
        <McpCatalogPicker onSelect={handleCatalogSelect} disabled={isPending} />
        <div className="flex shrink-0 flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => setManual(true)}
            data-testid="remote-mcp-manual-url-toggle"
            className="cursor-pointer text-sm font-medium text-foreground underline decoration-border underline-offset-4 transition hover:decoration-foreground"
          >
            {t('catalog.manual')}
          </button>
          <a
            href={MCP_SUGGESTION_MAILTO}
            onClick={() => capture(PostHogEventEnum.RemoteMcpCatalogSuggestionClicked)}
            data-testid="remote-mcp-suggest-integration"
            className="text-xs text-foreground-subtle transition-colors hover:text-muted-foreground"
          >
            {t('catalog.suggest')}
          </a>
        </div>
      </div>
    </Form>
  );
};

/** Editable connection details for an existing action. The primary connection state lives in
 *  the OAuth status panel above; here we only expose the URL + headers (and, for non-OAuth
 *  actions, a reconnect button). */
const ConnectionDetails = ({ connect }: { connect: ConnectHook }) => {
  const { t } = useTranslation('remote_mcp');
  const { form, submitFrom, isPending, oauth } = connect;
  const busy = oauth.phase === 'authorizing' || oauth.phase === 'finalizing';
  const hideConnectButton =
    oauth.phase !== 'idle' || (!!oauth.providerLabel && !oauth.allowRegularProbe);

  return (
    <Form {...form}>
      <div className="flex flex-col gap-4">
        <UrlField connect={connect} disabled={busy} />
        <ApiDataTable name={HttpItemCategory.HEADERS} form={form} withInsertBadge={false} />
        {!hideConnectButton && (
          <div className="mt-2 flex">
            {isPending ? (
              <Spinner className="size-5" />
            ) : (
              <Button
                variant="dark"
                onClick={() => void submitFrom('reconnect')}
                data-testid="remote-mcp-action-sheet-connect-button"
              >
                {t('button.reconnect')}
              </Button>
            )}
          </div>
        )}
      </div>
    </Form>
  );
};

type ConnectRemoteMcpProps = {
  remoteMcpAction?: RemoteMcpDto;
  withAccordion: boolean;
};

export const ConnectRemoteMcpForm = ({ remoteMcpAction, withAccordion }: ConnectRemoteMcpProps) => {
  const { t } = useTranslation('remote_mcp');
  const connect = useConnectRemoteMcpActionForm(remoteMcpAction);
  const { oauth } = connect;

  // Brand-new action: the focused, in-place connect flow (no accordion, no stacked panels).
  if (!remoteMcpAction) {
    return <NewConnectionFlow connect={connect} />;
  }

  const oauthGoverned = oauth.phase !== 'idle' || !!remoteMcpAction.oauth;
  const statusPanel = oauthGoverned ? (
    <RemoteMcpOAuthStatus
      phase={oauth.phase}
      providerLabel={oauth.providerLabel}
      connectedStatus={remoteMcpAction.oauth?.status}
      onConnect={oauth.openConsentPopup}
      onReconnect={oauth.reconnect}
      onDisconnect={oauth.disconnect}
      onCancel={oauth.cancel}
      isDisconnecting={oauth.isDisconnecting}
    />
  ) : null;

  if (!withAccordion) {
    return (
      <div className="flex flex-col gap-4">
        {statusPanel}
        <ConnectionDetails connect={connect} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {statusPanel}
      <Accordion
        type="single"
        collapsible
        className="w-full"
        data-testid="knowledge-base-action-sheet-advanced-options-accordion"
      >
        <AccordionItem
          value="connection_details"
          className="rounded-xl border border-border py-2 px-6"
        >
          <AccordionTrigger className="text-foreground font-medium px-1">
            <div className="flex items-center gap-3">
              <Settings className="size-4" /> {t('form.connection_details')}
            </div>
          </AccordionTrigger>
          <AccordionContent className="border-t pt-4 flex flex-col gap-10 px-1">
            <ConnectionDetails connect={connect} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
